import { useRef, useState } from "react";

/**
 * Records a voice message from the microphone via the browser MediaRecorder API.
 * `stop()` resolves with a File ready to hand to `useDirectUploadToS3`. The backend
 * transcodes the recording server-side, so the browser default format is fine.
 */
export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunksRef.current.push(e.data);
    };
    recorder.start();
    recorderRef.current = recorder;
    setIsRecording(true);
  };

  const stop = (): Promise<File> =>
    new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder) {
        setIsRecording(false);
        resolve(new File([], "voice.webm", { type: "audio/webm" }));
        return;
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("ogg")
          ? "ogg"
          : type.includes("mp4")
            ? "m4a"
            : "webm";
        recorder.stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        resolve(new File([blob], `voice-${Date.now()}.${ext}`, { type }));
      };
      recorder.stop();
    });

  const cancel = () => {
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      recorder.stop();
      recorder.stream.getTracks().forEach((track) => track.stop());
    }
    chunksRef.current = [];
    setIsRecording(false);
  };

  return { isRecording, start, stop, cancel };
};
